import { useRef, type CSSProperties } from 'react';
import type { Project } from '../../lib/types';
import { formatRelativeTime, needsSchedule, projectPoster } from '../../lib/project';
import { projectPipelineId, projectStageMeta } from '../../lib/stages';
import { Badge } from '../../ui/kit/components/primitives';
import { DropdownMenu } from '../../ui/kit/components/navigation';
import { Icon, type IconName } from '../../ui/kit/components/icons';
import '../../ui/styles/components.css';
import './projects.css';

export const PIPELINE_ICONS: Record<string, IconName> = { film: 'film', vfx: 'layers', marketing: 'sparkle', saas: 'monitor', upscale: 'expand' };
const PIPELINE_LABELS: Record<string, string> = { film: 'Film', vfx: 'VFX', marketing: 'Marketing', saas: 'SaaS', upscale: 'Upscale' };

function projectDetail(project: Project, kind: string) {
  const series = project.seriesLink as { role?: string; episodeCount?: number } | undefined;
  if (series?.role === 'series') return `Series · ${series.episodeCount || 0} episode${series.episodeCount === 1 ? '' : 's'}`;
  if (kind === 'saas') return `${(project.saasWorkspace as { beats?: unknown[] } | undefined)?.beats?.length || 0} scenes`;
  if (kind === 'upscale') return `${(project.upscaleWorkspace?.clips || []).length} clips`;
  if (kind === 'marketing') return `${(project.marketingWorkspace?.creativeAssets?.productPhotos || []).length} refs`;
  return `${project.shots?.length || 0} shots`;
}

interface Props {
  project: Project;
  index: number;
  onOpen: () => void;
  onJobs: () => void;
  onExport: () => void;
  onDelete: () => void;
  onConvert?: () => void;
}

/** A standalone Micro drama can become a series; its film becomes Episode 1. */
export function canConvertToSeries(project: Project) {
  return /micro\s*drama/i.test(String(project.type || '')) && !project.seriesLink && project.projectKind !== 'series' && !(project.seriesConversion as { seriesId?: string } | undefined)?.seriesId && !['reviewer', 'client'].includes(String(project.collabRole || ''));
}

export default function ProjectTile({ project, index, onOpen, onJobs, onExport, onDelete, onConvert }: Props) {
  const video = useRef<HTMLVideoElement>(null);
  const meta = projectStageMeta(project);
  const poster = projectPoster(project);
  const kind = projectPipelineId(project);
  const kindTitle = PIPELINE_LABELS[kind] || 'Film';
  const detail = projectDetail(project, kind);
  const total = meta.stages.length;
  const unscheduled = needsSchedule(project);
  const isSeries = (project.seriesLink as { role?: string } | undefined)?.role === 'series';
  const preview = (play: boolean) => {
    const node = video.current;
    if (!node) return;
    if (play) { void node.play().catch(() => {}); return; }
    node.pause();
    node.currentTime = 0;
  };

  return <article className="studio-project-card" style={{ '--i': Math.min(index, 12) } as CSSProperties} onPointerEnter={() => preview(true)} onPointerLeave={() => preview(false)}>
    <div className="studio-project-poster" aria-hidden="true">
      {poster.kind === 'video' && poster.url ? <video ref={video} src={poster.url} muted loop playsInline preload="metadata" /> : poster.url ? <img src={poster.url} alt="" loading="lazy" /> : <span className="studio-project-placeholder"><Icon name={PIPELINE_ICONS[kind] || 'film'} size={28} /></span>}
      <span className="studio-project-format bs-mono">{project.format || '16:9'}</span>
      <span className="studio-project-go"><Icon name="arrow" size={16} /></span>
    </div>
    <div className="studio-project-body">
      <div className="studio-project-heading">
        <div className="studio-project-labels">
          <span className={`studio-project-kind studio-project-kind--${kind}`}><Icon name={PIPELINE_ICONS[kind] || 'film'} size={12} />{kindTitle}</span>
          {unscheduled && <Badge tone="warning">Needs date</Badge>}
        </div>
        <h3><button type="button" className="studio-project-open" onClick={onOpen} aria-label={`Open ${project.name} — ${detail}, ${meta.info.plain}`}>{project.name}</button></h3>
      </div>
      <div className="studio-project-progress">
        <span className="studio-stage-meter" aria-hidden="true">{meta.stages.map((stage, i) => <i key={stage.id} className={i < meta.current ? 'is-done' : i === meta.current ? 'is-current' : undefined} />)}</span>
        <span className="studio-stage-label"><span className="bs-mono">{String(meta.current + 1).padStart(2, '0')}/{String(total).padStart(2, '0')}</span>{meta.info.plain}</span>
      </div>
      <p className="studio-project-meta bs-mono">{detail} · {formatRelativeTime(project.updatedAt)}</p>
      <div className="studio-project-actions">
        <DropdownMenu label={`Actions for ${project.name}`} items={[
          { label: 'Open production', icon: 'arrow', onSelect: onOpen },
          { label: 'View jobs', icon: 'layers', onSelect: onJobs },
          { label: isSeries ? 'Download series archive' : 'Export archive', icon: 'download', onSelect: onExport },
          ...(onConvert && canConvertToSeries(project) ? [{ label: 'Convert to series', icon: 'layers' as const, onSelect: onConvert }] : []),
          { label: isSeries ? 'Delete series…' : 'Delete production', icon: 'trash', danger: true, onSelect: onDelete }
        ]} />
      </div>
    </div>
  </article>;
}
